<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Codes Promo</h2>
          <p class="page-subtitle">{{ filtered.length }} / {{ codes.length }} codes</p>
        </div>
        <div class="header-actions">
          <el-button type="primary" :icon="Plus" @click="openCreate">Nouveau code</el-button>
          <el-button @click="load" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <!-- ── Filters bar ── -->
      <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px;margin-top:20px;align-items:center">
        <el-input
          v-model="search"
          placeholder="Rechercher un code…"
          clearable
          :prefix-icon="Search"
          style="width:200px"
        />
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="→"
          start-placeholder="Créé après"
          end-placeholder="Créé avant"
          format="DD/MM/YYYY"
          style="width:260px"
          clearable
        />
        <el-select v-model="statusFilter" placeholder="Statut" clearable style="width:130px">
          <el-option label="Tous" value="" />
          <el-option label="Actif" value="active" />
          <el-option label="Inactif" value="inactive" />
          <el-option label="Expiré" value="expired" />
        </el-select>
        <el-select v-model="typeFilter" placeholder="Type" clearable style="width:130px">
          <el-option label="Tous" value="" />
          <el-option label="Montant fixe" value="FIXED" />
          <el-option label="Pourcentage" value="PERCENT" />
        </el-select>
        <el-button link @click="resetFilters" v-if="hasFilters">
          <el-icon><Close /></el-icon> Réinitialiser
        </el-button>
      </div>

      <!-- ── Batch action bar ── -->
      <transition name="el-fade-in">
        <div
          v-if="selection.length"
          style="display:flex;align-items:center;gap:12px;padding:10px 14px;margin-bottom:10px;background:var(--el-color-primary-light-9);border:1px solid var(--el-color-primary-light-5);border-radius:8px"
        >
          <span style="font-weight:600;color:var(--el-color-primary)">
            {{ selection.length }} sélectionné(s)
          </span>
          <el-button size="small" :icon="CircleCheck" @click="batchActivate">Activer</el-button>
          <el-button size="small" :icon="CircleClose" @click="batchDeactivate">Désactiver</el-button>
          <el-button size="small" type="danger" :icon="Delete" @click="batchDelete">Supprimer</el-button>
          <el-button size="small" link @click="tableRef?.clearSelection()">Tout désélectionner</el-button>
        </div>
      </transition>

      <div v-if="isLoading" class="flex justify-center p-12">
        <el-icon class="is-loading text-4xl text-profissa-accent"><Loading /></el-icon>
      </div>

      <el-empty v-else-if="!filtered.length" description="Aucun code correspondant" />

      <el-table
        v-else
        ref="tableRef"
        :data="filtered"
        stripe
        style="width:100%"
        @selection-change="selection = $event"
      >
        <el-table-column type="selection" width="44" />
        <el-table-column prop="code" label="Code" min-width="130">
          <template #default="{ row }">
            <span class="font-mono font-bold text-profissa-accent">{{ row.code }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Remise" min-width="120">
          <template #default="{ row }">
            <el-tag type="success" v-if="row.discount_type === 'FIXED'">-{{ row.discount_value }} FCFA</el-tag>
            <el-tag type="warning" v-else>-{{ row.discount_value }}%</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Utilisations" min-width="110">
          <template #default="{ row }">
            {{ row.uses_count }} / {{ row.max_uses ?? '∞' }}
          </template>
        </el-table-column>
        <el-table-column label="Expiration" min-width="140">
          <template #default="{ row }">
            <span v-if="row.expires_at" :class="isExpired(row.expires_at) ? 'text-red-400' : 'text-gray-300'">
              {{ fmtDate(row.expires_at) }}
            </span>
            <span v-else class="text-gray-500">Aucune</span>
          </template>
        </el-table-column>
        <el-table-column label="Statut" width="100">
          <template #default="{ row }">
            <el-tag :type="row.is_active && !isExpired(row.expires_at) ? 'success' : 'danger'" size="small">
              {{ row.is_active && !isExpired(row.expires_at) ? 'Actif' : 'Inactif' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Créé le" min-width="140">
          <template #default="{ row }">{{ fmtDate(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="" width="60" fixed="right" align="center">
          <template #default="{ row }">
            <el-dropdown trigger="click" @command="(cmd) => handleAction(cmd, row)" @click.stop>
              <el-button size="small" :loading="togglingId === row.id" :icon="MoreFilled" circle @click.stop />
              <template #dropdown>
                <el-dropdown-menu @click.stop>
                  <el-dropdown-item command="copy" :icon="CopyDocument">Copier le code</el-dropdown-item>
                  <el-dropdown-item command="edit" :icon="Edit">Modifier</el-dropdown-item>
                  <el-dropdown-item command="whatsapp">
                    <span style="display:flex;align-items:center;gap:8px">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </span>
                  </el-dropdown-item>
                  <el-dropdown-item command="share" :icon="Share">Autres…</el-dropdown-item>
                  <el-dropdown-item divided command="toggle" :icon="row.is_active ? CircleClose : CircleCheck">
                    {{ row.is_active ? 'Désactiver' : 'Activer' }}
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" :icon="Delete" style="color:var(--el-color-danger)">
                    Supprimer
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Create dialog -->
    <el-dialog append-to-body v-model="createDialogVisible" title="Nouveau code promo" width="480px" :close-on-click-modal="false">
      <el-form :model="form" label-position="top" :rules="rules" ref="formRef">
        <el-form-item label="Code" prop="code">
          <div style="display:flex;gap:8px;width:100%">
            <el-input
              v-model="form.code"
              placeholder="Ex: BIENVENUE2000"
              style="text-transform:uppercase;flex:1"
              @input="form.code = form.code.toUpperCase()"
            />
            <el-button @click="generateCode" :icon="MagicStick" title="Générer automatiquement">Auto</el-button>
          </div>
        </el-form-item>
        <el-form-item label="Type de remise" prop="discount_type">
          <el-radio-group v-model="form.discount_type">
            <el-radio value="FIXED">Montant fixe (FCFA)</el-radio>
            <el-radio value="PERCENT">Pourcentage (%)</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="form.discount_type === 'FIXED' ? 'Montant (FCFA)' : 'Pourcentage (%)'" prop="discount_value">
          <el-input-number v-model="form.discount_value" :min="1" :max="form.discount_type === 'PERCENT' ? 100 : 1000000" style="width:100%" />
        </el-form-item>
        <el-form-item label="Utilisations max (vide = illimité)">
          <el-input-number v-model="form.max_uses" :min="1" :controls="false" placeholder="Illimité" style="width:100%" />
        </el-form-item>
        <el-form-item label="Date d'expiration (optionnel)">
          <el-date-picker v-model="form.expires_at" type="datetime" placeholder="Sélectionner une date" style="width:100%" format="DD/MM/YYYY HH:mm" />
        </el-form-item>
        <div v-if="createError" class="text-red-400 text-sm mt-2">{{ createError }}</div>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">Annuler</el-button>
        <el-button type="primary" :loading="isCreating" @click="submitCreate">Créer</el-button>
      </template>
    </el-dialog>

    <el-dialog append-to-body v-model="editDialogVisible" title="Modifier le code promo" width="480px" :close-on-click-modal="false">
      <el-form :model="editForm" label-position="top">
        <el-form-item label="Code">
          <el-input :model-value="editTarget?.code || ''" disabled />
        </el-form-item>
        <el-form-item label="Type de remise">
          <el-input :model-value="editTarget ? (editTarget.discount_type === 'FIXED' ? 'Montant fixe' : 'Pourcentage') : ''" disabled />
        </el-form-item>
        <el-form-item label="Valeur de remise">
          <el-input :model-value="editTarget ? (editTarget.discount_type === 'FIXED' ? `${editTarget.discount_value} FCFA` : `${editTarget.discount_value}%`) : ''" disabled />
        </el-form-item>
        <el-form-item label="Utilisations max (vide = illimité)">
          <el-input-number v-model="editForm.max_uses" :min="1" :controls="false" placeholder="Illimité" style="width:100%" />
        </el-form-item>
        <el-form-item label="Date d'expiration (optionnel)">
          <el-date-picker v-model="editForm.expires_at" type="datetime" placeholder="Sélectionner une date" style="width:100%" format="DD/MM/YYYY HH:mm" />
        </el-form-item>
        <el-form-item label="Statut">
          <el-switch v-model="editForm.is_active" active-text="Actif" inactive-text="Inactif" />
        </el-form-item>
        <div v-if="editError" class="text-red-400 text-sm mt-2">{{ editError }}</div>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">Annuler</el-button>
        <el-button type="primary" :loading="isEditing" @click="submitEdit">Enregistrer</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { Plus, Refresh, Loading, Delete, MagicStick, MoreFilled, CircleClose, CircleCheck, Search, Close, CopyDocument, Share, Edit } from '@element-plus/icons-vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import { listPromoCodes, createPromoCode, updatePromoCode, deletePromoCode } from '../services/adminApi'

// ── State ──────────────────────────────────────────────────────────────────
const codes = ref([])
const isLoading = ref(false)
const togglingId = ref(null)
const selection = ref([])
const tableRef = ref(null)

// Filters
const search = ref('')
const statusFilter = ref('')
const typeFilter = ref('')
const dateRange = ref(null)

const hasFilters = computed(() =>
  search.value || statusFilter.value || typeFilter.value || dateRange.value
)

const filtered = computed(() => {
  let list = codes.value
  if (search.value) {
    const q = search.value.toUpperCase()
    list = list.filter(c => c.code.includes(q))
  }
  if (typeFilter.value) {
    list = list.filter(c => c.discount_type === typeFilter.value)
  }
  if (statusFilter.value === 'active')   list = list.filter(c => c.is_active && !isExpired(c.expires_at))
  if (statusFilter.value === 'inactive') list = list.filter(c => !c.is_active)
  if (statusFilter.value === 'expired')  list = list.filter(c => isExpired(c.expires_at))
  if (dateRange.value?.[0]) {
    const from = new Date(dateRange.value[0])
    const to   = new Date(dateRange.value[1]); to.setHours(23, 59, 59)
    list = list.filter(c => {
      const d = new Date(c.created_at)
      return d >= from && d <= to
    })
  }
  return list
})

const resetFilters = () => {
  search.value = ''
  statusFilter.value = ''
  typeFilter.value = ''
  dateRange.value = null
}

// Create dialog
const createDialogVisible = ref(false)
const isCreating = ref(false)
const createError = ref('')
const formRef = ref(null)
const form = reactive({ code: '', discount_type: 'FIXED', discount_value: 2000, max_uses: null, expires_at: null })
const editDialogVisible = ref(false)
const editTarget = ref(null)
const isEditing = ref(false)
const editError = ref('')
const editForm = reactive({ max_uses: null, expires_at: null, is_active: true })
const rules = {
  code: [{ required: true, message: 'Le code est requis', trigger: 'blur' }],
  discount_type: [{ required: true }],
  discount_value: [{ required: true, type: 'number', min: 1 }],
}

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
const isExpired = (expiresAt) => !!expiresAt && new Date(expiresAt) < new Date()

// ── Load ───────────────────────────────────────────────────────────────────
const load = async () => {
  isLoading.value = true
  try { codes.value = await listPromoCodes() }
  catch { ElMessage.error('Erreur lors du chargement') }
  finally { isLoading.value = false }
}

// ── Row actions ────────────────────────────────────────────────────────────
const handleAction = (cmd, row) => {
  if (cmd === 'copy')      copyCode(row.code)
  else if (cmd === 'edit') openEdit(row)
  else if (cmd === 'whatsapp') shareWhatsApp(row)
  else if (cmd === 'share')    shareCode(row)
  else if (cmd === 'toggle')   toggleActive(row)
  else if (cmd === 'delete')   confirmDelete(row)
}

const shareWhatsApp = (row) => {
  const discount = row.discount_type === 'FIXED'
    ? `-${row.discount_value} FCFA`
    : `-${row.discount_value}%`
  const text = `Code promo ProFissa : *${row.code}* (${discount} sur votre prochaine demande)`
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
}

const copyCode = async (code) => {
  try {
    await navigator.clipboard.writeText(code)
    ElMessage.success(`Code "${code}" copié dans le presse-papier`)
  } catch {
    ElMessage.error('Impossible de copier')
  }
}

const shareCode = async (row) => {
  const discount = row.discount_type === 'FIXED'
    ? `-${row.discount_value} FCFA`
    : `-${row.discount_value}%`
  const text = `Code promo ProFissa : ${row.code} (${discount} sur votre prochaine demande)`
  if (navigator.share) {
    try { await navigator.share({ title: 'Code promo ProFissa', text }) }
    catch { /* user cancelled */ }
  } else {
    await copyCode(text)
    ElMessage.info('Lien de partage copié (Web Share API non disponible)')
  }
}

const toggleActive = async (row) => {
  togglingId.value = row.id
  try {
    const updated = await updatePromoCode(row.id, { is_active: !row.is_active })
    const idx = codes.value.findIndex(c => c.id === row.id)
    if (idx !== -1) codes.value[idx] = updated
    ElMessage.success(updated.is_active ? 'Code activé' : 'Code désactivé')
  } catch { ElMessage.error('Erreur') }
  finally { togglingId.value = null }
}

const confirmDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`Supprimer le code "${row.code}" définitivement ?`, 'Confirmation', { type: 'warning', confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler' })
    await deletePromoCode(row.id)
    codes.value = codes.value.filter(c => c.id !== row.id)
    ElMessage.success('Code supprimé')
  } catch { /* cancelled */ }
}

// ── Batch actions ──────────────────────────────────────────────────────────
const batchActivate = async () => {
  await Promise.all(selection.value.map(row => updatePromoCode(row.id, { is_active: true })))
  await load()
  tableRef.value?.clearSelection()
  ElMessage.success(`${selection.value.length} code(s) activé(s)`)
}

const batchDeactivate = async () => {
  await Promise.all(selection.value.map(row => updatePromoCode(row.id, { is_active: false })))
  await load()
  tableRef.value?.clearSelection()
  ElMessage.success(`${selection.value.length} code(s) désactivé(s)`)
}

const batchDelete = async () => {
  const n = selection.value.length
  try {
    await ElMessageBox.confirm(`Supprimer ${n} code(s) définitivement ?`, 'Confirmation', { type: 'warning', confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler' })
    await Promise.all(selection.value.map(row => deletePromoCode(row.id)))
    await load()
    tableRef.value?.clearSelection()
    ElMessage.success(`${n} code(s) supprimé(s)`)
  } catch { /* cancelled */ }
}

// ── Create ─────────────────────────────────────────────────────────────────
const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const seg = (n) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  form.code = `${seg(4)}-${seg(4)}`
}

const openCreate = () => {
  Object.assign(form, { code: '', discount_type: 'FIXED', discount_value: 2000, max_uses: null, expires_at: null })
  createError.value = ''
  createDialogVisible.value = true
}

const openEdit = (row) => {
  editTarget.value = row
  editError.value = ''
  editForm.max_uses = row.max_uses
  editForm.expires_at = row.expires_at ? new Date(row.expires_at) : null
  editForm.is_active = row.is_active
  editDialogVisible.value = true
}

const submitCreate = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    isCreating.value = true
    createError.value = ''
    try {
      const payload = { code: form.code, discount_type: form.discount_type, discount_value: form.discount_value }
      if (form.max_uses) payload.max_uses = form.max_uses
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString()
      const created = await createPromoCode(payload)
      codes.value.unshift(created)
      createDialogVisible.value = false
      ElMessage.success(`Code "${created.code}" créé !`)
    } catch (e) {
      createError.value = e?.response?.data?.detail || 'Erreur lors de la création'
    } finally {
      isCreating.value = false
    }
  })
}

const submitEdit = async () => {
  if (!editTarget.value) return
  isEditing.value = true
  editError.value = ''
  try {
    const payload = {
      is_active: editForm.is_active,
      max_uses: editForm.max_uses || null,
      expires_at: editForm.expires_at ? new Date(editForm.expires_at).toISOString() : null,
    }
    const updated = await updatePromoCode(editTarget.value.id, payload)
    const idx = codes.value.findIndex(c => c.id === updated.id)
    if (idx !== -1) codes.value[idx] = updated
    editDialogVisible.value = false
    ElMessage.success(`Code "${updated.code}" mis à jour`)
  } catch (e) {
    editError.value = e?.response?.data?.detail || 'Erreur lors de la mise à jour'
  } finally {
    isEditing.value = false
  }
}

onMounted(load)
</script>
